/**
 * VoorinspectieDetailPageWb — Werkbank-render van het voorinspectieformulier.
 *
 * Zelfde hook (useVoorinspectieItem) en dus exact dezelfde save-payload als de
 * legacy-render. Volgorde (plan 2026-09-02): foto bovenaan, feiten, vorm en
 * maat, staat in groepen, soort en merk, risico + los item als rij, notities
 * ingeklapt, Opslaan / Volgende vast onderin.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  VOORINSPECTIE_OPTIONS,
  VOORINSPECTIE_GROEPEN,
  RISICO_INDICATIE_VOORINSPECTIE,
  SOORT_OPTIONS,
  MERK_OPTIONS,
} from '../lib/constants';
import { CameraCapture } from '../components/CameraCapture';
import { MediaStrip } from '../components/MediaStrip';
import { RisicoMeldDialog } from '../components/RisicoMeldDialog';
import { ReparatieIntentCard } from '../components/ReparatieIntentCard';
import { BerichtenInline } from '../components/berichten/BerichtenInline';
import { KlachtBanner } from '../components/KlachtBanner';
import { KlantEigenItemCard } from '../components/KlantEigenItemCard';
import { KleedTijdlijnLijst } from '../components/KleedTijdlijnLijst';
import { WbButton, WbFactRow, WbFlag, WbModal, WbTile, WbToggleGroup, StickyActionBar } from '../components/wb';
import { useHeader } from '../stores';
import { useVoorinspectieItem } from './useVoorinspectieItem';
import type { VormType } from '../lib/salesforce';

const VORM_OPTIES: { value: VormType; label: string }[] = [
  { value: 'Rechthoek', label: 'Rechthoek' },
  { value: 'Rond', label: 'Rond' },
  { value: 'Anders', label: 'Anders' },
];

/** Vijf meest gekozen soorten in 2026 (mirror_wasserij_item.soort, gemeten 02-09). */
const SOORT_SNELKEUZE = ['Synthetisch tapijt', 'Perzisch tapijt', 'Wol tapijt', 'India getuft tapijt', 'Bolletjes tapijt'] as const;
const SOORT_ANDERS = '__anders__';

function Sectie({ titel, children, className }: { titel: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={clsx('space-y-2', className)}>
      <h2 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">{titel}</h2>
      {children}
    </section>
  );
}

function MaatVeld({ label, value, onChange, placeholder, step }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  step?: string;
}) {
  return (
    <label className="block min-h-tap bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 py-1.5">
      <span className="block text-[11px] font-bold uppercase tracking-wide text-wb-inkt2 leading-tight">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="wb-focus w-full bg-transparent font-mono text-xl font-semibold text-wb-inkt placeholder:text-wb-inkt3 outline-none"
      />
    </label>
  );
}

const RISICO_LABEL: Record<string, string> = {
  Gemeld: 'Gemeld · wacht op kantoor',
  Bevestigd: 'Bevestigd door kantoor',
  Wacht_klant: 'Wacht op klant',
  Akkoord: 'Klant gaf akkoord',
  Niet_Akkoord: 'Klant zegt nee · actie nodig',
  Specialist_Akkoord: 'Specialist akkoord',
  Alternatief_gekozen: 'Alternatief gekozen',
};

export function VoorinspectieDetailPageWb() {
  const vi = useVoorinspectieItem();
  const { item, allItems, isLoading, error } = vi;
  const [notitiesOpen, setNotitiesOpen] = useState(false);
  const [losItemOpen, setLosItemOpen] = useState(false);

  useHeader(item ? item.itemName : 'Voorinspectie', item && allItems.length > 1 ? `${vi.positie} / ${allItems.length}` : null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="font-mono text-wb-inkt2">Item laden…</p>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="space-y-3">
        <Link to="/voorinspectie" className="wb-focus inline-flex items-center min-h-tap font-semibold underline underline-offset-4 text-wb-inkt">
          ‹ Terug naar lijst
        </Link>
        <WbFlag kind="geld" title="Kon item niet laden">{error || 'Item niet gevonden'}</WbFlag>
      </div>
    );
  }

  const soortIsSnel = (SOORT_SNELKEUZE as readonly string[]).includes(vi.soort);
  const soortToggleValue = vi.soort === '' ? null : soortIsSnel ? vi.soort : SOORT_ANDERS;
  // "Anders…" zet soort op 'Anders' (een echte SOORT_OPTION); elke niet-snelkeuze toont de volledige lijst.
  const toonSoortSelect = vi.soort !== '' && !soortIsSnel;

  const risicoStatus = item.risicoStatus && item.risicoStatus !== 'Geen' ? item.risicoStatus : null;
  const risicoTypes = (item.risicoTypes || '').split(';').filter(Boolean);
  const notitiesGevuld = vi.voorinspectieOpmerkingen.trim() !== '' || vi.wasrapport.trim() !== '';
  const notitiesZichtbaar = notitiesOpen || notitiesGevuld;
  const opgegevenMaat = item.lengthCm && item.widthCm
    ? `${item.lengthCm} × ${item.widthCm}`
    : item.diameterCm
      ? `Ø ${item.diameterCm}`
      : vi.existingOppervlakte
        ? `${vi.existingOppervlakte.toFixed(2)} m²`
        : '–';
  const pakket = item.secondaryItems.find((s) => s.category === 'Package')?.productName;
  const extras = item.secondaryItems.filter((s) => s.category !== 'Package');

  return (
    <div className="space-y-5 pb-[140px]">
      {vi.saveFout && <WbFlag kind="geld" title="Niet opgeslagen">{vi.saveFout}</WbFlag>}
      {/* Terug + WI groot */}
      <div className="flex items-center justify-between gap-2">
        <Link to="/voorinspectie" className="wb-focus inline-flex items-center gap-1 min-h-tap font-mono text-xl font-semibold text-wb-inkt">
          <span aria-hidden>‹</span> {item.itemName}
        </Link>
        {allItems.length > 1 && (
          <span className="font-mono text-sm font-semibold bg-wb-inkt text-white rounded-wb px-2 py-1">
            {vi.positie} / {allItems.length}
          </span>
        )}
      </div>

      {item.isKlacht && <KlachtBanner reden={item.klachtReden} />}
      <ReparatieIntentCard item={item} onChanged={() => vi.loadItem({ silent: true })} />

      {/* Foto bovenaan */}
      <Sectie titel="Foto">
        {/* Altijd de eigen camera: CompanyCam is uit de wasserij weg (media-laag fase 1+2). */}
        <button
          type="button"
          onClick={() => vi.setShowCamera(true)}
          className="wb-focus w-full min-h-[150px] rounded-wb-lg border-wb border-dashed border-wb-rand bg-wb-vlak flex flex-col items-center justify-center gap-2 text-wb-inkt"
        >
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="font-wb-heading font-bold uppercase tracking-wide">Foto van het hele kleed</span>
        </button>
        <p className="text-sm text-wb-inkt2">Eén foto van iets verder weg met het hele kleed in beeld, franjes erbij. Van dichtbij mag daarna.</p>
        <MediaStrip wiSfId={item.itemId} refreshKey={vi.mediaRefresh} />
      </Sectie>

      {/* Feiten */}
      <WbFactRow
        facts={[
          { label: 'Opgegeven', value: opgegevenMaat },
          { label: 'Pakket', value: pakket ?? '–', tekst: true },
          { label: 'Order', value: item.werkOrderName },
        ]}
      />
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-1 -mt-3">
          {extras.map((e) => (
            <span key={e.itemId} className="text-xs font-semibold px-2 py-1 rounded-[4px] border border-wb-rand text-wb-inkt">{e.productName}</span>
          ))}
        </div>
      )}
      {item.tijdlijn && item.tijdlijn.length > 0 ? (
        <section className="space-y-2">
          <h2 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Tijdlijn</h2>
          <KleedTijdlijnLijst stappen={item.tijdlijn} />
        </section>
      ) : item.wasrapport && (
        <WbFlag kind="info" title="Bestaand wasrapport">{item.wasrapport}</WbFlag>
      )}

      {/* Berichten: alleen als er iets is; de composer zit in BerichtenInline zelf */}
      <BerichtenInline
        scope="wi"
        werkOrderId={item.werkOrderId}
        werkOrderName={item.werkOrderName}
        wasserijItemId={item.itemId}
        wasserijItemName={item.itemName}
      />

      {/* Vorm + maat */}
      <Sectie titel="Vorm">
        <WbToggleGroup options={VORM_OPTIES} value={vi.vorm} onChange={vi.handleVormChange} label="Vorm" />
      </Sectie>
      <Sectie titel="Maat">
        <div className="grid grid-cols-3 gap-2">
          {vi.vorm === 'Rechthoek' && (
            <>
              <MaatVeld label="Lengte cm" value={vi.lengthCm} onChange={vi.setLengthCm} placeholder="300" />
              <MaatVeld label="Breedte cm" value={vi.widthCm} onChange={vi.setWidthCm} placeholder="200" />
            </>
          )}
          {vi.vorm === 'Rond' && (
            <MaatVeld label="Diameter cm" value={vi.diameterCm} onChange={vi.setDiameterCm} placeholder="250" />
          )}
          {vi.vorm === 'Anders' && (
            <MaatVeld label="Oppervlakte m²" value={vi.aantal} onChange={vi.setAantal} placeholder="0.00" step="0.01" />
          )}
          <div className="min-h-tap bg-wb-inkt text-white rounded-wb px-3 py-1.5 flex flex-col justify-center">
            <span className="text-[11px] font-bold uppercase tracking-wide text-wb-kopink2 leading-tight">m²</span>
            <span className="font-mono text-xl font-semibold">
              {vi.vorm === 'Anders'
                ? (vi.aantal ? parseFloat(vi.aantal).toFixed(2) : '–')
                : vi.calculatedOppervlakte
                  ? vi.calculatedOppervlakte.toFixed(2)
                  : '–'}
            </span>
          </div>
        </div>
        {vi.vorm === 'Anders' && (
          <p className="text-sm text-wb-inkt2">Onregelmatige vorm: vul een schatting van de oppervlakte in.</p>
        )}
      </Sectie>

      {/* Staat in groepen */}
      <Sectie titel="Staat">
        {VOORINSPECTIE_GROEPEN.map((groep) => {
          const opties = VOORINSPECTIE_OPTIONS.filter((o) => o.groep === groep.key);
          return (
            <div key={groep.key} className="space-y-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wide text-wb-inkt3 pt-1">{groep.label}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                {opties.map((o) => (
                  <WbTile
                    key={o.value}
                    selected={vi.selectedVoorinspectie.includes(o.value)}
                    onToggle={() => vi.toggleVoorinspectie(o.value)}
                    wide={o.value === 'Geen bijzonderheden.'}
                  >
                    {o.label}
                  </WbTile>
                ))}
              </div>
            </div>
          );
        })}
        {vi.selectedVoorinspectie.length > 0 && (
          <p className="font-mono text-sm text-wb-inkt2">{vi.selectedVoorinspectie.length} geselecteerd</p>
        )}
      </Sectie>

      {/* Soort + merk */}
      <Sectie titel="Soort">
        <WbToggleGroup
          options={[...SOORT_SNELKEUZE.slice(0, 3).map((s) => ({ value: s, label: s.replace(' tapijt', '') }))]}
          value={soortToggleValue}
          onChange={vi.setSoort}
          label="Soort"
        />
        <WbToggleGroup
          options={[
            ...SOORT_SNELKEUZE.slice(3).map((s) => ({ value: s, label: s.replace(' tapijt', '') })),
            { value: SOORT_ANDERS, label: 'Anders…' },
          ]}
          value={soortToggleValue}
          onChange={(v) => {
            if (v === SOORT_ANDERS) {
              // Open de volledige lijst; kies de eerste niet-snelkeuze als startwaarde zodat de select zichtbaar wordt.
              if (soortIsSnel || vi.soort === '') vi.setSoort('Anders');
            } else {
              vi.setSoort(v);
            }
          }}
          label="Soort, meer"
        />
        {toonSoortSelect && (
          <select
            value={vi.soort}
            onChange={(e) => vi.setSoort(e.target.value)}
            className="wb-focus w-full min-h-tap bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 text-base font-semibold text-wb-inkt"
          >
            <option value="">-- Selecteer soort --</option>
            {SOORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
      </Sectie>
      <Sectie titel="Merk">
        <select
          value={vi.merk}
          onChange={(e) => vi.setMerk(e.target.value)}
          className="wb-focus w-full min-h-tap bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 text-base font-semibold text-wb-inkt"
        >
          <option value="">-- Selecteer merk --</option>
          {MERK_OPTIONS.map((o) => (
            <option key={o.value || 'none'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Sectie>

      {/* Risico + los item */}
      {risicoStatus && (
        <WbFlag kind={risicoStatus === 'Niet_Akkoord' ? 'geld' : 'info'} title={`Risico · ${RISICO_LABEL[risicoStatus] ?? risicoStatus}`}>
          {risicoTypes.length > 0 && <span className="block">{risicoTypes.join(', ')}</span>}
          {item.risicoToelichting && <span className="block">"{item.risicoToelichting}"</span>}
          {item.risicoKlantReactie && <span className="block">Klant: "{item.risicoKlantReactie}"</span>}
          {risicoStatus === 'Niet_Akkoord' && <span className="block">Bel de klant voor een alternatief, of laat kantoor het kleed annuleren.</span>}
          {risicoStatus === 'Gemeld' && <span className="block">Kantoor beoordeelt je melding. Rond je voorinspectie gerust af.</span>}
        </WbFlag>
      )}
      <div className="grid grid-cols-2 gap-2">
        <WbButton variant="danger" onClick={() => vi.setRisicoDialogOpen(true)}>
          {risicoStatus === 'Niet_Akkoord' ? 'Opnieuw melden' : risicoStatus ? 'Risico gemeld' : 'Risico melden'}
        </WbButton>
        <WbButton variant="secondary" onClick={() => setLosItemOpen((v) => !v)}>
          Los item taggen
        </WbButton>
      </div>
      {losItemOpen && <KlantEigenItemCard item={item} onAdded={() => vi.loadItem({ silent: true })} />}

      {/* Notities ingeklapt */}
      <Sectie titel="Notities">
        {!notitiesZichtbaar ? (
          <WbButton variant="link" onClick={() => setNotitiesOpen(true)}>Notitie toevoegen ›</WbButton>
        ) : (
          <div className="space-y-2">
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-wb-inkt2 mb-1">Voor de klant (komt in het rapport)</span>
              <textarea
                value={vi.voorinspectieOpmerkingen}
                onChange={(e) => vi.setVoorinspectieOpmerkingen(e.target.value)}
                rows={3}
                placeholder="Opmerkingen voor klant…"
                className="wb-focus w-full bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 py-2 text-base text-wb-inkt resize-none"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-wb-inkt2 mb-1">Intern (wasrapport)</span>
              <textarea
                value={vi.wasrapport}
                onChange={(e) => vi.setWasrapport(e.target.value)}
                rows={3}
                placeholder="Interne bijzonderheden…"
                className="wb-focus w-full bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 py-2 text-base text-wb-inkt resize-none"
              />
            </label>
          </div>
        )}
      </Sectie>

      {/* Vaste knoppen */}
      {/* Eén knop: Opslaan gaat terug naar de lijst. "Volgende" werd niet gebruikt (Willem 02-09). */}
      <StickyActionBar>
        <WbButton size="lg" onClick={() => vi.handleSave(false)} isLoading={vi.isSaving}>
          Opslaan
        </WbButton>
      </StickyActionBar>

      {/* Camera */}
      {vi.showCamera && (
        <CameraCapture
          fase="voorinspectie"
          doel={{ wiSfId: item.itemId }}
          subtitle={item.itemName}
          onClose={(aantal) => {
            vi.setShowCamera(false);
            if (aantal > 0) vi.setMediaRefresh((k) => k + 1);
          }}
        />
      )}

      {/* Afrond-guard: schade aangevinkt, geen melding — eerst kiezen */}
      <WbModal
        open={vi.risicoAfrondPrompt !== null}
        onClose={() => vi.setRisicoAfrondPrompt(null)}
        titel="Schade aangevinkt, geen risico gemeld"
        acties={
          <>
            <WbButton
              variant="secondary"
              size="lg"
              onClick={() => {
                const prompt = vi.risicoAfrondPrompt;
                vi.setRisicoAfrondPrompt(null);
                if (prompt) vi.handleSave(prompt.goToNext, { negeerRisicoIndicatie: true });
              }}
            >
              Zonder melding
            </WbButton>
            <WbButton variant="danger" size="lg" onClick={() => { vi.setRisicoAfrondPrompt(null); vi.setRisicoDialogOpen(true); }}>
              Eerst melden
            </WbButton>
          </>
        }
      >
        <p className="text-base text-wb-inkt2">
          Je vinkte aan:{' '}
          <strong className="text-wb-inkt">
            {vi.selectedVoorinspectie
              .filter((v) => RISICO_INDICATIE_VOORINSPECTIE.has(v))
              .map((v) => VOORINSPECTIE_OPTIONS.find((o) => o.value === v)?.label ?? v)
              .join(', ')}
          </strong>
          . Aanvinken is geen melding: zonder risico-melding loopt het kleed gewoon door naar reinigen, zonder akkoord van de klant.
        </p>
      </WbModal>

      {vi.risicoDialogOpen && (
        <RisicoMeldDialog
          itemId={item.itemId}
          itemName={item.itemName}
          companyCamProjectId={item.companyCamProjectId}
          onClose={() => vi.setRisicoDialogOpen(false)}
          onSubmitted={vi.markeerGemeld}
        />
      )}
    </div>
  );
}
